import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Pagination, Tabs } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    formatAmount,
    formatBalance,
    formatSignedAmount,
    paymentMethodLabelKey,
    statementRowVariant,
    type Supplier,
} from '../suppliersMeta';
import type { useSupplierDetailDrawer } from './useSupplierDetailDrawer';

type SupplierDetailDrawerViewProps = {
    /** True when the signed-in user may withdraw a payment (OrgAdmin only). */
    canDeletePayments: boolean;
    drawer: ReturnType<typeof useSupplierDetailDrawer>;
    onClose: () => void;
    onRecordPayment: () => void;
    supplier: Supplier | null;
};

const AMOUNT_VARIANT_CLASS = {
    neutral: 'text-muted',
    success: 'text-success-soft-foreground',
    danger: 'text-danger-soft-foreground',
} as const;

/**
 * Right-side supplier account drawer: the Owed / Paid / Balance header, then
 * the running statement and the payments list as two tabs.
 *
 * Render only. The two tables are real `<table>`s rather than a DataGrid —
 * they are read-only, never sorted and never paged client-side, so the grid's
 * machinery would buy nothing and cost the semantics a screen reader needs.
 */
export const SupplierDetailDrawerView: React.FC<
    SupplierDetailDrawerViewProps
> = ({ canDeletePayments, drawer, onClose, onRecordPayment, supplier }) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            owed: t('suppliers.components.supplierDetailDrawer.owed'),
            paid: t('suppliers.components.supplierDetailDrawer.paid'),
            balance: t('suppliers.components.supplierDetailDrawer.balance'),
            openInvoices: t(
                'suppliers.components.supplierDetailDrawer.openInvoices',
            ),
            onAccount: t('suppliers.components.supplierDetailDrawer.onAccount'),
            onAccountHint: t(
                'suppliers.components.supplierDetailDrawer.onAccountHint',
            ),
            recordPayment: t(
                'suppliers.components.supplierDetailDrawer.recordPayment',
            ),
            tabsAria: t('suppliers.components.supplierDetailDrawer.tabsAria'),
            statementTab: t(
                'suppliers.components.supplierDetailDrawer.statementTab',
            ),
            paymentsTab: t(
                'suppliers.components.supplierDetailDrawer.paymentsTab',
            ),
            loading: t('suppliers.components.supplierDetailDrawer.loading'),
            loadError: t('suppliers.components.supplierDetailDrawer.loadError'),
            retry: t('suppliers.components.supplierDetailDrawer.retry'),
            statement: {
                caption: t(
                    'suppliers.components.supplierDetailDrawer.statement.caption',
                ),
                date: t(
                    'suppliers.components.supplierDetailDrawer.statement.date',
                ),
                document: t(
                    'suppliers.components.supplierDetailDrawer.statement.document',
                ),
                amount: t(
                    'suppliers.components.supplierDetailDrawer.statement.amount',
                ),
                running: t(
                    'suppliers.components.supplierDetailDrawer.statement.running',
                ),
                opening: t(
                    'suppliers.components.supplierDetailDrawer.statement.opening',
                ),
                closing: t(
                    'suppliers.components.supplierDetailDrawer.statement.closing',
                ),
                invoice: t(
                    'suppliers.components.supplierDetailDrawer.statement.invoice',
                ),
                payment: t(
                    'suppliers.components.supplierDetailDrawer.statement.payment',
                ),
                unallocated: t(
                    'suppliers.components.supplierDetailDrawer.statement.unallocated',
                ),
                empty: t(
                    'suppliers.components.supplierDetailDrawer.statement.empty',
                ),
            },
            payments: {
                caption: t(
                    'suppliers.components.supplierDetailDrawer.payments.caption',
                ),
                date: t(
                    'suppliers.components.supplierDetailDrawer.payments.date',
                ),
                method: t(
                    'suppliers.components.supplierDetailDrawer.payments.method',
                ),
                reference: t(
                    'suppliers.components.supplierDetailDrawer.payments.reference',
                ),
                invoice: t(
                    'suppliers.components.supplierDetailDrawer.payments.invoice',
                ),
                amount: t(
                    'suppliers.components.supplierDetailDrawer.payments.amount',
                ),
                remove: t(
                    'suppliers.components.supplierDetailDrawer.payments.remove',
                ),
                empty: t(
                    'suppliers.components.supplierDetailDrawer.payments.empty',
                ),
                previous: t(
                    'suppliers.components.supplierDetailDrawer.payments.previous',
                ),
                next: t(
                    'suppliers.components.supplierDetailDrawer.payments.next',
                ),
            },
            fullyPaid: t(
                'suppliers.components.supplierTable.balance.fullyPaid',
            ),
        }),
        [t],
    );

    const balance = supplier
        ? formatBalance(supplier.balanceTetri, copy.fullyPaid)
        : null;

    return (
        <Sheet
            isOpen={supplier !== null}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[640px]">
                    <Sheet.Dialog
                        aria-label={supplier?.name ?? copy.tabsAria}
                        {...getNodeTestIdProp('supplier-detail-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span
                                    className="text-lg font-semibold"
                                    {...getNodeTestIdProp(
                                        'supplier-detail-name',
                                    )}
                                >
                                    {supplier?.name ?? ''}
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body className="flex flex-col gap-6">
                            <div
                                className="grid grid-cols-2 gap-4 rounded-xl bg-surface-secondary p-4 sm:grid-cols-4"
                                {...getNodeTestIdProp('supplier-detail-totals')}
                            >
                                <Figure
                                    label={copy.owed}
                                    slot="supplier-detail-owed"
                                    value={formatAmount(
                                        supplier?.owedTotalTetri ?? 0,
                                    )}
                                />
                                <Figure
                                    label={copy.paid}
                                    slot="supplier-detail-paid"
                                    value={formatAmount(
                                        supplier?.paidTotalTetri ?? 0,
                                    )}
                                />
                                <Figure
                                    label={copy.onAccount}
                                    slot="supplier-detail-unallocated"
                                    value={formatAmount(
                                        supplier?.unallocatedTotalTetri ?? 0,
                                    )}
                                />
                                <Figure
                                    className={
                                        balance
                                            ? AMOUNT_VARIANT_CLASS[
                                                  balance.variant
                                              ]
                                            : undefined
                                    }
                                    label={copy.balance}
                                    slot="supplier-detail-balance"
                                    value={balance?.text ?? ''}
                                />
                            </div>

                            {/* Why the Purchases list and this balance can
                                differ. Always shown, so a reader who checks the
                                two against each other finds the rule rather
                                than a discrepancy (BOS-161). */}
                            <p
                                className="-mt-4 text-[11px] text-muted"
                                {...getNodeTestIdProp(
                                    'supplier-detail-on-account-hint',
                                )}
                            >
                                {copy.onAccountHint}
                            </p>

                            <div className="flex items-center justify-between gap-3">
                                <span
                                    className="text-xs text-muted"
                                    {...getNodeTestIdProp(
                                        'supplier-detail-open-invoices',
                                    )}
                                >
                                    {copy.openInvoices}
                                    {': '}
                                    {supplier?.openInvoiceCount ?? 0}
                                </span>
                                <Button
                                    onPress={onRecordPayment}
                                    size="sm"
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'supplier-detail-record-payment',
                                    )}
                                >
                                    <Icon icon="gravity-ui:plus" />
                                    {copy.recordPayment}
                                </Button>
                            </div>

                            <Tabs
                                className="w-full"
                                onSelectionChange={(key) =>
                                    drawer.onTabChange(String(key))
                                }
                                selectedKey={drawer.tab}
                                variant="secondary"
                            >
                                <Tabs.ListContainer className="min-w-0 max-w-max">
                                    <Tabs.List aria-label={copy.tabsAria}>
                                        <Tabs.Tab
                                            className="whitespace-nowrap"
                                            id="statement"
                                            {...getNodeTestIdProp(
                                                'supplier-detail-tab-statement',
                                            )}
                                        >
                                            {copy.statementTab}
                                            <Tabs.Indicator />
                                        </Tabs.Tab>
                                        <Tabs.Tab
                                            className="whitespace-nowrap"
                                            id="payments"
                                            {...getNodeTestIdProp(
                                                'supplier-detail-tab-payments',
                                            )}
                                        >
                                            {copy.paymentsTab}
                                            <Tabs.Indicator />
                                        </Tabs.Tab>
                                    </Tabs.List>
                                </Tabs.ListContainer>

                                <Tabs.Panel className="pt-4" id="statement">
                                    {drawer.statementError ? (
                                        <Notice
                                            message={copy.loadError}
                                            onRetry={drawer.onRetry}
                                            retryLabel={copy.retry}
                                            slot="supplier-detail-statement-error"
                                        />
                                    ) : drawer.isLoadingStatement ||
                                      drawer.statement === null ? (
                                        <Loading
                                            label={copy.loading}
                                            slot="supplier-detail-statement-loading"
                                        />
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table
                                                className="w-full text-sm"
                                                {...getNodeTestIdProp(
                                                    'supplier-detail-statement-table',
                                                )}
                                            >
                                                <caption className="sr-only">
                                                    {copy.statement.caption}
                                                </caption>
                                                <thead>
                                                    <tr className="border-b border-separator text-left text-xs text-muted">
                                                        <th
                                                            className="py-2 pr-3 font-medium"
                                                            scope="col"
                                                        >
                                                            {
                                                                copy.statement
                                                                    .date
                                                            }
                                                        </th>
                                                        <th
                                                            className="py-2 pr-3 font-medium"
                                                            scope="col"
                                                        >
                                                            {
                                                                copy.statement
                                                                    .document
                                                            }
                                                        </th>
                                                        <th
                                                            className="py-2 pr-3 text-right font-medium"
                                                            scope="col"
                                                        >
                                                            {
                                                                copy.statement
                                                                    .amount
                                                            }
                                                        </th>
                                                        <th
                                                            className="py-2 text-right font-medium"
                                                            scope="col"
                                                        >
                                                            {
                                                                copy.statement
                                                                    .running
                                                            }
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="border-b border-separator text-muted">
                                                        <td
                                                            className="py-2 pr-3"
                                                            colSpan={3}
                                                        >
                                                            {
                                                                copy.statement
                                                                    .opening
                                                            }
                                                        </td>
                                                        <td
                                                            className="py-2 text-right tabular-nums"
                                                            {...getNodeTestIdProp(
                                                                'supplier-detail-statement-opening',
                                                            )}
                                                        >
                                                            {formatSignedAmount(
                                                                drawer.statement
                                                                    .openingBalanceTetri,
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {drawer.statement.rows.map(
                                                        (row) => (
                                                            <tr
                                                                className="border-b border-separator"
                                                                key={`${row.kind}-${row.documentId}`}
                                                                {...getNodeTestIdProp(
                                                                    `supplier-detail-statement-row-${row.documentId}`,
                                                                )}
                                                            >
                                                                <td className="py-2 pr-3 tabular-nums">
                                                                    {row.date}
                                                                </td>
                                                                <td className="py-2 pr-3">
                                                                    <span className="text-foreground">
                                                                        {row.kind ===
                                                                        'Invoice'
                                                                            ? copy
                                                                                  .statement
                                                                                  .invoice
                                                                            : copy
                                                                                  .statement
                                                                                  .payment}
                                                                    </span>
                                                                    {row.reference ? (
                                                                        <span className="ml-1 text-muted">
                                                                            {
                                                                                row.reference
                                                                            }
                                                                        </span>
                                                                    ) : null}
                                                                    {row.unallocated ? (
                                                                        <span className="ml-1 text-xs text-muted">
                                                                            {
                                                                                copy
                                                                                    .statement
                                                                                    .unallocated
                                                                            }
                                                                        </span>
                                                                    ) : null}
                                                                </td>
                                                                <td
                                                                    className={`py-2 pr-3 text-right tabular-nums ${AMOUNT_VARIANT_CLASS[statementRowVariant(row)]}`}
                                                                >
                                                                    {formatSignedAmount(
                                                                        row.amountTetri,
                                                                    )}
                                                                </td>
                                                                <td className="py-2 text-right tabular-nums text-foreground">
                                                                    {formatSignedAmount(
                                                                        row.runningBalanceTetri,
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ),
                                                    )}
                                                    {drawer.statement.rows
                                                        .length === 0 ? (
                                                        <tr>
                                                            <td
                                                                className="py-6 text-center text-muted"
                                                                colSpan={4}
                                                                {...getNodeTestIdProp(
                                                                    'supplier-detail-statement-empty',
                                                                )}
                                                            >
                                                                {
                                                                    copy
                                                                        .statement
                                                                        .empty
                                                                }
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="font-medium">
                                                        <td
                                                            className="py-2 pr-3"
                                                            colSpan={3}
                                                        >
                                                            {
                                                                copy.statement
                                                                    .closing
                                                            }
                                                        </td>
                                                        <td
                                                            className="py-2 text-right tabular-nums"
                                                            {...getNodeTestIdProp(
                                                                'supplier-detail-statement-closing',
                                                            )}
                                                        >
                                                            {formatSignedAmount(
                                                                drawer.statement
                                                                    .closingBalanceTetri,
                                                            )}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </Tabs.Panel>

                                <Tabs.Panel className="pt-4" id="payments">
                                    {drawer.paymentsError ? (
                                        <Notice
                                            message={copy.loadError}
                                            onRetry={drawer.onRetry}
                                            retryLabel={copy.retry}
                                            slot="supplier-detail-payments-error"
                                        />
                                    ) : drawer.isLoadingPayments ||
                                      drawer.payments === null ? (
                                        <Loading
                                            label={copy.loading}
                                            slot="supplier-detail-payments-loading"
                                        />
                                    ) : (
                                        <div className="flex flex-col gap-3">
                                            <div className="overflow-x-auto">
                                                <table
                                                    className="w-full text-sm"
                                                    {...getNodeTestIdProp(
                                                        'supplier-detail-payments-table',
                                                    )}
                                                >
                                                    <caption className="sr-only">
                                                        {copy.payments.caption}
                                                    </caption>
                                                    <thead>
                                                        <tr className="border-b border-separator text-left text-xs text-muted">
                                                            <th
                                                                className="py-2 pr-3 font-medium"
                                                                scope="col"
                                                            >
                                                                {
                                                                    copy
                                                                        .payments
                                                                        .date
                                                                }
                                                            </th>
                                                            <th
                                                                className="py-2 pr-3 font-medium"
                                                                scope="col"
                                                            >
                                                                {
                                                                    copy
                                                                        .payments
                                                                        .method
                                                                }
                                                            </th>
                                                            <th
                                                                className="py-2 pr-3 font-medium"
                                                                scope="col"
                                                            >
                                                                {
                                                                    copy
                                                                        .payments
                                                                        .invoice
                                                                }
                                                            </th>
                                                            <th
                                                                className="py-2 pr-3 text-right font-medium"
                                                                scope="col"
                                                            >
                                                                {
                                                                    copy
                                                                        .payments
                                                                        .amount
                                                                }
                                                            </th>
                                                            {canDeletePayments ? (
                                                                <th
                                                                    className="py-2"
                                                                    scope="col"
                                                                >
                                                                    <span className="sr-only">
                                                                        {
                                                                            copy
                                                                                .payments
                                                                                .remove
                                                                        }
                                                                    </span>
                                                                </th>
                                                            ) : null}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {drawer.payments.map(
                                                            (payment) => (
                                                                <tr
                                                                    className="border-b border-separator"
                                                                    key={
                                                                        payment.id
                                                                    }
                                                                    {...getNodeTestIdProp(
                                                                        `supplier-detail-payment-row-${payment.id}`,
                                                                    )}
                                                                >
                                                                    <td className="py-2 pr-3 tabular-nums">
                                                                        {
                                                                            payment.date
                                                                        }
                                                                    </td>
                                                                    <td className="py-2 pr-3">
                                                                        {t(
                                                                            paymentMethodLabelKey(
                                                                                payment.method,
                                                                            ),
                                                                        )}
                                                                    </td>
                                                                    <td className="py-2 pr-3 text-muted">
                                                                        {payment.purchaseInvoiceNumber ??
                                                                            copy
                                                                                .statement
                                                                                .unallocated}
                                                                    </td>
                                                                    <td className="py-2 pr-3 text-right tabular-nums text-success-soft-foreground">
                                                                        {formatAmount(
                                                                            payment.amountTetri,
                                                                        )}
                                                                    </td>
                                                                    {canDeletePayments ? (
                                                                        <td className="py-2 text-right">
                                                                            <Button
                                                                                isDisabled={
                                                                                    drawer.isDeleting
                                                                                }
                                                                                isIconOnly
                                                                                onPress={() =>
                                                                                    drawer.onDeletePayment(
                                                                                        payment.id,
                                                                                    )
                                                                                }
                                                                                size="sm"
                                                                                variant="tertiary"
                                                                                {...getNodeTestIdProp(
                                                                                    `supplier-detail-payment-delete-${payment.id}`,
                                                                                )}
                                                                            >
                                                                                <span className="sr-only">
                                                                                    {`${copy.payments.remove} ${payment.date}`}
                                                                                </span>
                                                                                <Icon icon="gravity-ui:trash-bin" />
                                                                            </Button>
                                                                        </td>
                                                                    ) : null}
                                                                </tr>
                                                            ),
                                                        )}
                                                        {drawer.payments
                                                            .length === 0 ? (
                                                            <tr>
                                                                <td
                                                                    className="py-6 text-center text-muted"
                                                                    colSpan={
                                                                        canDeletePayments
                                                                            ? 5
                                                                            : 4
                                                                    }
                                                                    {...getNodeTestIdProp(
                                                                        'supplier-detail-payments-empty',
                                                                    )}
                                                                >
                                                                    {
                                                                        copy
                                                                            .payments
                                                                            .empty
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ) : null}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {drawer.deleteError ? (
                                                <p
                                                    className="text-sm text-danger"
                                                    {...getNodeTestIdProp(
                                                        'supplier-detail-payment-delete-error',
                                                    )}
                                                >
                                                    {drawer.deleteError}
                                                </p>
                                            ) : null}
                                            {drawer.totalPages > 1 ? (
                                                <Pagination size="sm">
                                                    <Pagination.Content>
                                                        <Pagination.Item>
                                                            <Pagination.Previous
                                                                aria-label={
                                                                    copy
                                                                        .payments
                                                                        .previous
                                                                }
                                                                isDisabled={
                                                                    drawer.page ===
                                                                    1
                                                                }
                                                                onPress={() =>
                                                                    drawer.onPageChange(
                                                                        drawer.page -
                                                                            1,
                                                                    )
                                                                }
                                                            >
                                                                <Pagination.PreviousIcon />
                                                            </Pagination.Previous>
                                                        </Pagination.Item>
                                                        <Pagination.Item>
                                                            <Pagination.Next
                                                                aria-label={
                                                                    copy
                                                                        .payments
                                                                        .next
                                                                }
                                                                isDisabled={
                                                                    drawer.page ===
                                                                    drawer.totalPages
                                                                }
                                                                onPress={() =>
                                                                    drawer.onPageChange(
                                                                        drawer.page +
                                                                            1,
                                                                    )
                                                                }
                                                            >
                                                                <Pagination.NextIcon />
                                                            </Pagination.Next>
                                                        </Pagination.Item>
                                                    </Pagination.Content>
                                                </Pagination>
                                            ) : null}
                                        </div>
                                    )}
                                </Tabs.Panel>
                            </Tabs>
                        </Sheet.Body>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};

type FigureProps = {
    className?: string;
    label: string;
    slot: string;
    value: string;
};

const Figure: React.FC<FigureProps> = ({ className, label, slot, value }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs text-muted">{label}</span>
        <span
            className={`text-sm font-semibold tabular-nums ${className ?? 'text-foreground'}`}
            {...getNodeTestIdProp(slot)}
        >
            {value}
        </span>
    </div>
);

type LoadingProps = { label: string; slot: string };

const Loading: React.FC<LoadingProps> = ({ label, slot }) => (
    <div
        className="flex flex-col items-center gap-2 py-10 text-muted"
        {...getNodeTestIdProp(slot)}
    >
        <Icon
            aria-hidden
            className="size-7 animate-spin text-default-500"
            icon="gravity-ui:arrows-rotate-right"
        />
        <span className="text-sm">{label}</span>
    </div>
);

type NoticeProps = {
    message: string;
    onRetry: () => void;
    retryLabel: string;
    slot: string;
};

const Notice: React.FC<NoticeProps> = ({
    message,
    onRetry,
    retryLabel,
    slot,
}) => (
    <div
        className="flex flex-col items-center gap-3 py-10 text-muted"
        {...getNodeTestIdProp(slot)}
    >
        <span className="text-sm">{message}</span>
        <Button onPress={onRetry} size="sm" variant="tertiary">
            {retryLabel}
        </Button>
    </div>
);
